import { useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, CheckCircle, MessageCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { INDIAN_STATES } from '../config';
import useContact from '../hooks/useContact';
import SEO from '../components/SEO';
import { trackPixel } from '../lib/pixel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactPage() {
  const { t } = useLanguage();
  const { phone, phone2, email, address, hours, whatsappLink } = useContact();
  const c = t.contact;
  const [form, setForm] = useState({ name: '', phone: '', email: '', product: '', state: '', city: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) return;
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/inquiries`, { ...form, source: 'contact_form' });
      trackPixel('Lead', { content_name: form.product || 'Contact Form', content_category: 'contact_form' });
      setSuccess(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full px-4 py-3 border rounded-lg text-sm font-body outline-none transition-all duration-200 focus:ring-2 bg-white";

  return (
    <main>
      <SEO
        title="Contact Us"
        path="/contact"
        description="Get in touch with Shree Ram Agro Industries (ARV). Call, WhatsApp or visit our factory in Punjab for product inquiries about Rotavator, Disc Harrow, Mulcher and Cultivator."
        keywords="ARV contact, Shree Ram Agro Industries phone, agricultural machinery inquiry, ARV factory Punjab"
      />
      {/* Hero */}
      <section className="py-16 text-center" style={{ backgroundImage: 'linear-gradient(135deg, #0A3F22 0%, #1B6B3D 100%)' }}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mx-auto max-w-2xl px-4">
          <h1 className="font-heading font-black text-4xl sm:text-5xl text-white mt-2">{c.title}</h1>
          <p className="mt-4 text-white/70 text-base">{c.subtitle}</p>
        </motion.div>
      </section>

      <section className="py-16 lg:py-24" style={{ backgroundColor: '#F9F8F4' }}>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-10">

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-2 space-y-4"
            >
              {[
                { icon: MapPin, label: 'Address', value: address, href: null },
                { icon: Phone, label: 'Phone', value: phone, href: `tel:${phone}` },
                { icon: Phone, label: 'Phone', value: phone2, href: `tel:${phone2}` },
                { icon: Mail, label: 'Email', value: email, href: `mailto:${email}` },
                { icon: Clock, label: 'Hours', value: hours, href: null },
              ].map(({ icon: Icon, label, value, href }) => (
                <div key={`${label}-${value}`} className="flex items-start gap-4 p-5 bg-white rounded-xl border card-hover" style={{ borderColor: '#E5E7EB' }}>
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#EAF2EC' }}>
                    <Icon size={18} style={{ color: '#0A3F22' }} />
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: '#4A554A' }}>{label}</div>
                    {href ? (
                      <a href={href} className="text-sm font-medium transition-colors hover:underline" style={{ color: '#0A3F22' }}>{value}</a>
                    ) : (
                      <span className="text-sm font-medium" style={{ color: '#0A3F22' }}>{value}</span>
                    )}
                  </div>
                </div>
              ))}

              {/* WhatsApp Card */}
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp"
                className="flex items-center gap-4 p-5 rounded-xl text-white transition-all duration-200 hover:scale-105"
                style={{ backgroundColor: '#25D366' }}
              >
                <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                  <MessageCircle size={20} className="text-white" />
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider opacity-80">WhatsApp</div>
                  <div className="text-sm font-bold">{c.info.whatsapp}</div>
                </div>
              </a>

              {/* Google Maps Embed */}
              <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #CDDFD2' }}>
                <iframe
                  title="SHREE RAM AGRO INDUSTRIES Location"
                  src="https://maps.google.com/maps?q=Phase+7+Focal+Point+Ludhiana+Punjab+141010+India&output=embed&z=15"
                  width="100%"
                  height="220"
                  style={{ border: 0, display: 'block' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  data-testid="contact-map-embed"
                />
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-3"
            >
              <div className="bg-white rounded-2xl shadow-sm border p-8" style={{ borderColor: '#E5E7EB' }}>
                <h2 className="font-heading font-bold text-2xl mb-6" style={{ color: '#0A3F22' }}>Send Us a Message</h2>

                {success ? (
                  <div className="text-center py-10">
                    <CheckCircle size={56} className="mx-auto mb-4" style={{ color: '#1B6B3D' }} />
                    <h3 className="font-heading font-bold text-xl mb-2" style={{ color: '#0A3F22' }}>Message Sent!</h3>
                    <p className="text-sm" style={{ color: '#4A554A' }}>{c.form.success}</p>
                    <button
                      onClick={() => { setSuccess(false); setForm({ name: '', phone: '', email: '', product: '', state: '', city: '', message: '' }); }}
                      className="mt-4 px-6 py-2 rounded-lg text-white text-sm font-semibold"
                      style={{ backgroundColor: '#0A3F22' }}
                    >
                      Send Another
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: '#4A554A' }}>{c.form.name} *</label>
                        <input data-testid="contact-name" type="text" required value={form.name}
                          onChange={(e) => setForm({ ...form, name: e.target.value })}
                          placeholder="Your full name" className={inputClass} style={{ borderColor: '#E5E7EB' }} />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: '#4A554A' }}>{c.form.phone} *</label>
                        <input data-testid="contact-phone" type="tel" required value={form.phone}
                          onChange={(e) => setForm({ ...form, phone: e.target.value })}
                          placeholder="+91 XXXXX XXXXX" className={inputClass} style={{ borderColor: '#E5E7EB' }} />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: '#4A554A' }}>{c.form.email}</label>
                        <input data-testid="contact-email" type="email" value={form.email}
                          onChange={(e) => setForm({ ...form, email: e.target.value })}
                          placeholder="your@email.com" className={inputClass} style={{ borderColor: '#E5E7EB' }} />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: '#4A554A' }}>{c.form.product}</label>
                        <select data-testid="contact-product" value={form.product}
                          onChange={(e) => setForm({ ...form, product: e.target.value })}
                          className={inputClass} style={{ borderColor: '#E5E7EB' }}>
                          <option value="">Select product</option>
                          <option value="Rotavator">Rotavator</option>
                          <option value="Mini Rotavator">Mini Rotavator</option>
                          <option value="Disk Harrow">Disk Harrow</option>
                          <option value="Mulcher">Mulcher</option>
                          <option value="Fertilizer Spreader">Fertilizer Spreader</option>
                          <option value="Haloom Mulcher (Potato Mulcher)">Haloom Mulcher (Potato Mulcher)</option>
                          <option value="Spring Tine Cultivator">Spring Tine Cultivator</option>
                          <option value="General Enquiry">General Enquiry</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: '#4A554A' }}>{c.form.state}</label>
                        <select data-testid="contact-state" value={form.state}
                          onChange={(e) => setForm({ ...form, state: e.target.value })}
                          className={inputClass} style={{ borderColor: '#E5E7EB' }}>
                          <option value="">Select state</option>
                          {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: '#4A554A' }}>City</label>
                        <input type="text" value={form.city}
                          onChange={(e) => setForm({ ...form, city: e.target.value })}
                          placeholder="Your city" className={inputClass} style={{ borderColor: '#E5E7EB' }} />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: '#4A554A' }}>{c.form.message}</label>
                      <textarea data-testid="contact-message" rows={4} value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                        placeholder="Tell us about your requirement..."
                        className={inputClass} style={{ borderColor: '#E5E7EB' }} />
                    </div>

                    {error && <p className="text-red-600 text-xs">{error}</p>}

                    <button
                      data-testid="contact-submit"
                      type="submit"
                      disabled={loading}
                      className="w-full py-4 rounded-lg text-white font-heading font-bold text-base transition-opacity hover:opacity-90 disabled:opacity-60"
                      style={{ backgroundColor: '#0A3F22' }}
                    >
                      {loading ? 'Sending...' : c.form.submit}
                    </button>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Full-Width Map Section */}
      <section className="py-0" style={{ backgroundColor: '#F9F8F4' }}>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-2xl overflow-hidden shadow-sm"
            style={{ border: '1px solid #CDDFD2' }}
          >
            {/* Map Header */}
            <div className="flex items-center gap-3 px-5 py-3" style={{ backgroundColor: '#0A3F22' }}>
              <MapPin size={16} className="text-white/80" />
              <div>
                <span className="text-white font-semibold text-sm">SHREE RAM AGRO INDUSTRIES</span>
                <span className="text-white/60 text-xs ml-2">— Manufacturing &amp; Head Office</span>
              </div>
              <a
                href="https://maps.google.com/?q=Phase+7+Focal+Point+Ludhiana+Punjab+141010+India"
                target="_blank"
                rel="noopener noreferrer"
                className="ml-auto text-xs font-semibold px-3 py-1 rounded-full transition-colors"
                style={{ backgroundColor: '#D97706', color: '#fff' }}
                data-testid="contact-open-maps"
              >
                Open in Maps
              </a>
            </div>
            <iframe
              title="SHREE RAM AGRO INDUSTRIES Full Map"
              src="https://maps.google.com/maps?q=Phase+7+Focal+Point+Ludhiana+Punjab+141010+India&output=embed&z=15"
              width="100%"
              height="420"
              style={{ border: 0, display: 'block' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="contact-map-fullwidth"
            />
          </motion.div>
        </div>
      </section>
    </main>
  );
}
